"use client";

import { usePathname, useRouter } from "next/navigation";
import { useEffect, useRef } from "react";
import { useCart } from "~/hooks/cart";
import {
  useInitialPromoCode,
  useVoucherCodeRedemption,
} from "~/hooks/voucher-code-redemption";
import { Drawer } from "../Drawer/Drawer";
import VoucherCodeForm from "./VoucherCodeForm";
import { VoucherCodeOutcome } from "./VoucherCodeOutcome";

export function PromoCodeDrawer(props: {
  locationRef: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  /** Whether to pick up a promo code returned from login via the URL. */
  captureInitialCode: boolean;
}): React.ReactNode {
  const { locationRef, setOpen } = props;

  const router = useRouter();
  const pathname = usePathname();
  const { dispatchOption } = useCart(locationRef);

  const initialCode = useInitialPromoCode(props.captureInitialCode);

  const redemption = useVoucherCodeRedemption({
    locationRef,
    fulfillmentType: dispatchOption,
    initialCode,
    onClickVoucherContinue: () => {
      setOpen(false);
      router.push(`/${locationRef}/account/rewards`);
    },
  });

  const { hide } = redemption;

  useEffect(() => {
    if (initialCode) setOpen(true);
  }, [initialCode, setOpen]);

  const previousPathname = useRef(pathname);
  useEffect(() => {
    if (previousPathname.current === pathname) return;
    previousPathname.current = pathname;
    hide();
    setOpen(false);
  }, [pathname, hide, setOpen]);

  return (
    <>
      <Drawer
        title="Promo Code"
        open={props.open && !redemption.hasOutcome}
        onClose={() => {
          setOpen(false);
        }}
      >
        <VoucherCodeForm
          hideTitle
          voucherCode={redemption.value}
          isPending={redemption.isPending}
          onCodeChange={redemption.setValue}
          onSubmit={redemption.submit}
        />
      </Drawer>
      <VoucherCodeOutcome {...redemption.outcome} />
    </>
  );
}
